/**
 * Binary `snap` / `full` codec (docs §3), DataView little-endian.
 *
 *   u8  msgType            1 = snap, 2 = full
 *   hdr                    tick u32, credits i32, incomePerMin i16, areaShare u16 (×1e4),
 *                          flags u8, focus u16 (0xFFFF = none),
 *                          front u8 (status << 6 | ratio×10), frontRate i8 (hex/min)  → 18 B with msgType
 *   u16 sectionMask        bit i set ⇔ section i follows (empty sections cost nothing)
 *   sections, in order (each starts with a varint count):
 *     0 owners       list: [hex u16, owner u8]  | dense (hex = 0..n−1): RLE runs [owner u8, len varint]
 *     1 popRad       list: [hex u16, dens4|rad4 u8] | dense: dens4|rad4 byte per hex, PackBits-coded
 *     2 upserts      [id u32, kind u8, owner u8, state u8 (bit 7: detail follows), x i16, y i16 (×100),
 *                     vx i16, vy i16 (×1000), segTick u32, extra u8] + detail
 *     3 removes      [id u32, reason u8]
 *     4 tracks       [id u32, kind u8, owner u8, ox, oy, tx, ty i16 (×100), launchTick u32, flightTicks u16]
 *     5 hints        [hintId u32, x i16, y i16, bearing u16, spread u8 (½°), tick u32]
 *     6 detonations  [x i16, y i16, tick u32, exact u8]
 *     7 intercepts   [shooterId u32, targetId u32, fireTick u32, resolveTick u32, hit u8 (0 miss, 1 hit, 2 pending;
 *                     bit 4: an unseen shooter's origin hint follows: ox i16, oy i16 (×100))]
 *     8 shares       [playerId u8, areaShare u16, score u16] (public standings, 1 Hz)
 *     9 phase        [kind u8, a u8, b u8, tick u32]
 *    10 live         count = runs: [gap varint, len varint] (hex runs, ascending)
 *    11 ghosts       [id varint, kind u8, owner u8, flags u8 (1 heading, 2 extra), x i16, y i16, tick varint, heading u16?, extra u8?]
 *    12 allyPrompts  [from u8, to u8, expiresTick u32]
 *    13 lastSeen     count = hexes, then runs [value varint (seconds + 1, 0 = never), len varint] until count covered
 *
 * Owner detail (upsert state bit 7): u32 field mask, then the present fields of
 * OwnEntityDetail in DETAIL_FIELDS order (`route`: u8 waypoint count, then x i16, y i16 each).
 */
import {
  ENTITY_KINDS,
  ENTITY_VIEW_STATES,
  FRONT_STATUSES,
  REMOVE_REASONS,
  type AircraftPhase,
  type AirMission,
  type EntityView,
  type Ghost,
  type MissileKind,
  type OwnEntityDetail,
  type PhaseKind,
} from '@sa/sim';
import { angleFromWire, angleToWire, frontRateToWire, frontRatioFromWire, frontRatioToWire, posFromWire, posToWire, shareFromWire, shareToWire, spreadFromWire, spreadToWire, velFromWire, velToWire } from './quant.js';
import { emptySnapshot, type NetHeader, type NetSnapshot } from './snapshot.js';

export const MSG_SNAP = 1;
export const MSG_FULL = 2;

/** Wire order of PhaseKind (code = index). Append only. */
export const PHASE_KINDS: readonly PhaseKind[] = [
  'release',
  'final',
  'beacon',
  'eliminated',
  'ally',
  'allyBroken',
  'capitalsExposed',
  'capitalCritical',
  'capitalRecovered',
  'allyDeclined',
  'ceasefireOffer',
  'ceasefire',
  'ceasefireDeclined',
  'ceasefireBroken',
  'gift',
  'mark',
];
/** Wire order of AircraftPhase. Append only. */
export const AIRCRAFT_PHASES: readonly AircraftPhase[] = ['building', 'parked', 'rearming', 'outbound', 'onStation', 'intercept', 'returning'];
/** Wire order of AirMission (255 = null). Append only. */
export const AIR_MISSIONS: readonly AirMission[] = ['strike', 'patrol', 'scout'];

type DetailType = 'tick' | 'u8' | 'u32' | 'bool' | 'pos' | 'phase' | 'mission' | 'route';
/** Wire order of OwnEntityDetail fields (bit i of the mask). Append only. */
export const DETAIL_FIELDS: readonly (readonly [keyof OwnEntityDetail, DetailType])[] = [
  ['buildDoneTick', 'tick'],
  ['loaded', 'u8'],
  ['queued', 'u8'],
  ['loadDoneTick', 'tick'],
  ['cooldownUntil', 'tick'],
  ['ammo', 'u8'],
  ['reloadAt', 'tick'],
  ['level', 'u8'],
  ['upgradeDoneTick', 'tick'],
  ['autoScramble', 'bool'],
  ['baseId', 'u32'],
  ['phase', 'phase'],
  ['mission', 'mission'],
  ['tx', 'pos'],
  ['ty', 'pos'],
  ['readyTick', 'tick'],
  ['stationUntil', 'tick'],
  ['hp', 'u8'],
  ['torps', 'u8'],
  ['destX', 'pos'],
  ['destY', 'pos'],
  ['exposedUntil', 'tick'],
  ['braceUntil', 'tick'],
  ['braceReadyTick', 'tick'],
  ['priorityId', 'u32'],
  ['route', 'route'],
  ['docked', 'bool'],
  ['silent', 'bool'],
];

const S_OWNERS = 0;
const S_POPRAD = 1;
const S_UPSERTS = 2;
const S_REMOVES = 3;
const S_TRACKS = 4;
const S_HINTS = 5;
const S_DETS = 6;
const S_INTERCEPTS = 7;
const S_SHARES = 8;
const S_PHASE = 9;
const S_LIVE = 10;
const S_GHOSTS = 11;
const S_PROMPTS = 12;
const S_LASTSEEN = 13;

const DETAIL_BIT = 0x80;
/** hex indices are u16 */
const MAX_HEXES = 0x10000;
const NO_FOCUS = 0xffff;
const HIT_PENDING = 2;
/** intercept flags byte: an origin hint (ox, oy) follows */
const HINT_FLAG = 0x10;
const NULL_MISSION = 255;

const index = <T>(list: readonly T[], v: T, what: string): number => {
  const i = list.indexOf(v);
  if (i < 0) throw new Error(`codec: unknown ${what} ${String(v)}`);
  return i;
};
const at = <T>(list: readonly T[], i: number, what: string): T => {
  const v = list[i];
  if (v === undefined) throw new Error(`codec: bad ${what} code ${i}`);
  return v;
};
const u8 = (v: number) => (v < 0 ? 0 : v > 255 ? 255 : v | 0);

// ---------------------------------------------------------------------------
// Writer / reader
// ---------------------------------------------------------------------------

class Writer {
  buf: Uint8Array;
  dv: DataView;
  o = 0;
  constructor(cap = 256) {
    this.buf = new Uint8Array(cap);
    this.dv = new DataView(this.buf.buffer);
  }
  private need(n: number): void {
    if (this.o + n <= this.buf.length) return;
    let cap = this.buf.length * 2;
    while (cap < this.o + n) cap *= 2;
    const b = new Uint8Array(cap);
    b.set(this.buf.subarray(0, this.o));
    this.buf = b;
    this.dv = new DataView(b.buffer);
  }
  u8(v: number): void {
    this.need(1);
    this.dv.setUint8(this.o, v);
    this.o += 1;
  }
  u16(v: number): void {
    this.need(2);
    this.dv.setUint16(this.o, v, true);
    this.o += 2;
  }
  i16(v: number): void {
    this.need(2);
    this.dv.setInt16(this.o, v, true);
    this.o += 2;
  }
  u32(v: number): void {
    this.need(4);
    this.dv.setUint32(this.o, v >>> 0, true);
    this.o += 4;
  }
  i32(v: number): void {
    this.need(4);
    this.dv.setInt32(this.o, v | 0, true);
    this.o += 4;
  }
  varint(v: number): void {
    this.need(5);
    let x = v >>> 0;
    while (x >= 0x80) {
      this.buf[this.o++] = (x & 0x7f) | 0x80;
      x >>>= 7;
    }
    this.buf[this.o++] = x;
  }
  bytes(): Uint8Array {
    return this.buf.slice(0, this.o);
  }
}

class Reader {
  readonly dv: DataView;
  o = 0;
  constructor(readonly buf: Uint8Array) {
    this.dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  }
  u8(): number {
    return this.dv.getUint8(this.o++);
  }
  u16(): number {
    const v = this.dv.getUint16(this.o, true);
    this.o += 2;
    return v;
  }
  i16(): number {
    const v = this.dv.getInt16(this.o, true);
    this.o += 2;
    return v;
  }
  u32(): number {
    const v = this.dv.getUint32(this.o, true);
    this.o += 4;
    return v;
  }
  i32(): number {
    const v = this.dv.getInt32(this.o, true);
    this.o += 4;
    return v;
  }
  varint(): number {
    let x = 0;
    let shift = 0;
    for (;;) {
      if (this.o >= this.buf.length) throw new RangeError('codec: truncated varint');
      const b = this.buf[this.o++];
      x += (b & 0x7f) * 2 ** shift;
      if (b < 0x80) return x;
      shift += 7;
      if (shift > 28) throw new RangeError('codec: varint too long');
    }
  }
  /** Element count, sanity-checked against the bytes left (each element ≥ minBytes). */
  count(minBytes: number): number {
    const n = this.varint();
    if (n * minBytes > this.buf.length - this.o) throw new RangeError('codec: count exceeds buffer');
    return n;
  }
}

// ---------------------------------------------------------------------------
// Encode
// ---------------------------------------------------------------------------

function isDense(list: readonly { hex: number }[]): boolean {
  if (list.length === 0) return false;
  for (let i = 0; i < list.length; i++) if (list[i].hex !== i) return false;
  return true;
}

function writeDetail(w: Writer, d: OwnEntityDetail): void {
  let mask = 0;
  for (let i = 0; i < DETAIL_FIELDS.length; i++) if (d[DETAIL_FIELDS[i][0]] !== undefined) mask |= 1 << i;
  w.u32(mask);
  for (let i = 0; i < DETAIL_FIELDS.length; i++) {
    if (!(mask & (1 << i))) continue;
    const [key, type] = DETAIL_FIELDS[i];
    const v = d[key];
    switch (type) {
      case 'tick':
        w.i32(v as number);
        break;
      case 'u8':
        w.u8(u8(v as number));
        break;
      case 'u32':
        w.u32(v as number);
        break;
      case 'bool':
        w.u8(v ? 1 : 0);
        break;
      case 'pos':
        w.i16(posToWire(v as number));
        break;
      case 'phase':
        w.u8(index(AIRCRAFT_PHASES, v as AircraftPhase, 'aircraft phase'));
        break;
      case 'mission':
        w.u8(v === null ? NULL_MISSION : index(AIR_MISSIONS, v as AirMission, 'mission'));
        break;
      case 'route': {
        const xy = v as number[];
        const n = Math.min(255, xy.length >> 1);
        w.u8(n);
        for (let k = 0; k < n * 2; k++) w.i16(posToWire(xy[k]));
        break;
      }
    }
  }
}

function writeUpsert(w: Writer, e: EntityView): void {
  w.u32(e.id);
  w.u8(index(ENTITY_KINDS, e.kind, 'entity kind'));
  w.u8(e.owner);
  w.u8(index(ENTITY_VIEW_STATES, e.state, 'entity state') | (e.detail ? DETAIL_BIT : 0));
  w.i16(posToWire(e.x));
  w.i16(posToWire(e.y));
  w.i16(velToWire(e.vx));
  w.i16(velToWire(e.vy));
  w.u32(e.segTick);
  w.u8(u8(e.extra));
  if (e.detail) writeDetail(w, e.detail);
}

function writeGhost(w: Writer, g: Ghost): void {
  w.varint(g.id);
  w.u8(index(ENTITY_KINDS, g.kind, 'entity kind'));
  w.u8(g.owner);
  const flags = (g.heading !== undefined ? 1 : 0) | (g.extra !== undefined ? 2 : 0);
  w.u8(flags);
  w.i16(posToWire(g.x));
  w.i16(posToWire(g.y));
  w.varint(g.tick);
  if (g.heading !== undefined) w.u16(angleToWire(g.heading));
  if (g.extra !== undefined) w.u8(u8(g.extra));
}

function writeLive(w: Writer, hexes: readonly number[]): void {
  // count runs first
  let runs = 0;
  for (let i = 0; i < hexes.length; i++) if (i === 0 || hexes[i] !== hexes[i - 1] + 1) runs++;
  w.varint(runs);
  let prevEnd = 0;
  let i = 0;
  while (i < hexes.length) {
    const start = hexes[i];
    let j = i + 1;
    while (j < hexes.length && hexes[j] === hexes[j - 1] + 1) j++;
    if (start < prevEnd) throw new Error('codec: live hexes must be ascending and unique');
    w.varint(start - prevEnd);
    w.varint(j - i);
    prevEnd = start + (j - i);
    i = j;
  }
}

/**
 * PackBits: control byte c < 128 → c + 1 literal bytes follow; c ≥ 128 → the next byte
 * repeats c − 125 times (3..130).
 */
function writePackBits(w: Writer, b: Uint8Array): void {
  let i = 0;
  while (i < b.length) {
    let run = 1;
    while (i + run < b.length && run < 130 && b[i + run] === b[i]) run++;
    if (run >= 3) {
      w.u8(run + 125);
      w.u8(b[i]);
      i += run;
      continue;
    }
    // literal stretch up to the next run of ≥ 3
    let j = i;
    while (j < b.length && j - i < 128 && !(j + 2 < b.length && b[j] === b[j + 1] && b[j] === b[j + 2])) j++;
    w.u8(j - i - 1);
    for (let k = i; k < j; k++) w.u8(b[k]);
    i = j;
  }
}

function readPackBits(r: Reader, n: number): Uint8Array {
  const out = new Uint8Array(n);
  let o = 0;
  while (o < n) {
    const c = r.u8();
    if (c < 128) {
      if (o + c + 1 > n) throw new RangeError('codec: bad PackBits literal');
      for (let k = 0; k <= c; k++) out[o++] = r.u8();
    } else {
      const len = c - 125;
      if (o + len > n) throw new RangeError('codec: bad PackBits run');
      out.fill(r.u8(), o, o + len);
      o += len;
    }
  }
  return out;
}

function writeLastSeen(w: Writer, seen: Int32Array): void {
  w.varint(seen.length);
  let i = 0;
  while (i < seen.length) {
    const v = seen[i];
    let j = i + 1;
    while (j < seen.length && seen[j] === v) j++;
    w.varint(v < 0 ? 0 : Math.floor(v / 10) + 1);
    w.varint(j - i);
    i = j;
  }
}

export function encodeSnapshot(s: NetSnapshot): Uint8Array {
  const w = new Writer(256 + s.upserts.length * 24 + s.owners.length * 3 + s.popRad.length * 3);
  w.u8(s.full ? MSG_FULL : MSG_SNAP);
  const h = s.hdr;
  w.u32(h.tick);
  w.i32(h.credits);
  w.i16(Math.max(-32768, Math.min(32767, h.incomePerMin)));
  w.u16(shareToWire(h.areaShare));
  w.u8(h.flags);
  w.u16(h.focus < 0 ? NO_FOCUS : h.focus);
  w.u8((index(FRONT_STATUSES, h.front.status, 'front status') << 6) | frontRatioToWire(h.front.ratio));
  w.u8(frontRateToWire(h.front.rate) & 0xff);

  const present = [
    s.owners.length,
    s.popRad.length,
    s.upserts.length,
    s.removes.length,
    s.tracks.length,
    s.hints.length,
    s.detonations.length,
    s.intercepts.length,
    s.shares.length,
    s.phase.length,
    s.live.length,
    s.ghosts.length,
    s.allyPrompts.length,
    s.lastSeen ? s.lastSeen.length : 0,
  ];
  let mask = 0;
  for (let i = 0; i < present.length; i++) if (present[i] > 0) mask |= 1 << i;
  w.u16(mask);

  if (mask & (1 << S_OWNERS)) {
    const list = s.owners;
    w.varint(list.length);
    if (isDense(list)) {
      w.u8(1);
      let i = 0;
      while (i < list.length) {
        const o = list[i].owner;
        let j = i + 1;
        while (j < list.length && list[j].owner === o) j++;
        w.u8(o);
        w.varint(j - i);
        i = j;
      }
    } else {
      w.u8(0);
      for (const e of list) {
        w.u16(e.hex);
        w.u8(e.owner);
      }
    }
  }
  if (mask & (1 << S_POPRAD)) {
    const list = s.popRad;
    w.varint(list.length);
    const dense = isDense(list);
    w.u8(dense ? 1 : 0);
    if (dense) {
      const bytes = new Uint8Array(list.length);
      for (let i = 0; i < list.length; i++) bytes[i] = ((list[i].dens & 15) << 4) | (list[i].rad & 15);
      writePackBits(w, bytes);
    } else {
      for (const e of list) {
        w.u16(e.hex);
        w.u8(((e.dens & 15) << 4) | (e.rad & 15));
      }
    }
  }
  if (mask & (1 << S_UPSERTS)) {
    w.varint(s.upserts.length);
    for (const e of s.upserts) writeUpsert(w, e);
  }
  if (mask & (1 << S_REMOVES)) {
    w.varint(s.removes.length);
    for (const r of s.removes) {
      w.u32(r.id);
      w.u8(index(REMOVE_REASONS, r.reason, 'remove reason'));
    }
  }
  if (mask & (1 << S_TRACKS)) {
    w.varint(s.tracks.length);
    for (const t of s.tracks) {
      w.u32(t.id);
      w.u8(index(ENTITY_KINDS, t.kind, 'missile kind'));
      w.u8(t.owner);
      w.i16(posToWire(t.ox));
      w.i16(posToWire(t.oy));
      w.i16(posToWire(t.tx));
      w.i16(posToWire(t.ty));
      w.u32(t.launchTick);
      w.u16(t.flightTicks);
    }
  }
  if (mask & (1 << S_HINTS)) {
    w.varint(s.hints.length);
    for (const x of s.hints) {
      w.u32(x.hintId);
      w.i16(posToWire(x.x));
      w.i16(posToWire(x.y));
      w.u16(angleToWire(x.bearing));
      w.u8(spreadToWire(x.spread));
      w.u32(x.tick);
    }
  }
  if (mask & (1 << S_DETS)) {
    w.varint(s.detonations.length);
    for (const d of s.detonations) {
      w.i16(posToWire(d.x));
      w.i16(posToWire(d.y));
      w.u32(d.tick);
      w.u8(d.exact ? 1 : 0);
    }
  }
  if (mask & (1 << S_INTERCEPTS)) {
    w.varint(s.intercepts.length);
    for (const i of s.intercepts) {
      w.u32(i.shooterId);
      w.u32(i.targetId);
      w.u32(i.fireTick);
      w.u32(i.resolveTick);
      const hint = i.ox !== undefined && i.oy !== undefined;
      w.u8((i.hit === undefined ? HIT_PENDING : i.hit ? 1 : 0) | (hint ? HINT_FLAG : 0));
      if (hint) {
        w.i16(posToWire(i.ox!));
        w.i16(posToWire(i.oy!));
      }
    }
  }
  if (mask & (1 << S_SHARES)) {
    w.varint(s.shares.length);
    for (const x of s.shares) {
      w.u8(x.playerId);
      w.u16(shareToWire(x.areaShare));
      w.u16(Math.max(0, Math.min(0xffff, Math.round(x.score))));
    }
  }
  if (mask & (1 << S_PHASE)) {
    w.varint(s.phase.length);
    for (const p of s.phase) {
      w.u8(index(PHASE_KINDS, p.kind, 'phase kind'));
      w.u8(p.a);
      w.u8(p.b);
      w.u32(p.tick);
    }
  }
  if (mask & (1 << S_LIVE)) writeLive(w, s.live);
  if (mask & (1 << S_GHOSTS)) {
    w.varint(s.ghosts.length);
    for (const g of s.ghosts) writeGhost(w, g);
  }
  if (mask & (1 << S_PROMPTS)) {
    w.varint(s.allyPrompts.length);
    for (const r of s.allyPrompts) {
      w.u8(r.from);
      w.u8(r.to);
      w.u32(r.expiresTick);
    }
  }
  if (s.lastSeen && s.lastSeen.length > 0) writeLastSeen(w, s.lastSeen);
  return w.bytes();
}

// ---------------------------------------------------------------------------
// Decode
// ---------------------------------------------------------------------------

function readDetail(r: Reader): OwnEntityDetail {
  const mask = r.u32();
  const d: OwnEntityDetail = {};
  const out = d as Record<string, unknown>;
  for (let i = 0; i < DETAIL_FIELDS.length; i++) {
    if (!(mask & (1 << i))) continue;
    const [key, type] = DETAIL_FIELDS[i];
    switch (type) {
      case 'tick':
        out[key] = r.i32();
        break;
      case 'u8':
        out[key] = r.u8();
        break;
      case 'u32':
        out[key] = r.u32();
        break;
      case 'bool':
        out[key] = r.u8() === 1;
        break;
      case 'pos':
        out[key] = posFromWire(r.i16());
        break;
      case 'phase':
        out[key] = at(AIRCRAFT_PHASES, r.u8(), 'aircraft phase');
        break;
      case 'mission': {
        const m = r.u8();
        out[key] = m === NULL_MISSION ? null : at(AIR_MISSIONS, m, 'mission');
        break;
      }
      case 'route': {
        const n = r.u8() * 2;
        const xy: number[] = [];
        for (let k = 0; k < n; k++) xy.push(posFromWire(r.i16()));
        out[key] = xy;
        break;
      }
    }
  }
  return d;
}

function readUpsert(r: Reader): EntityView {
  const id = r.u32();
  const kind = at(ENTITY_KINDS, r.u8(), 'entity kind');
  const owner = r.u8();
  const st = r.u8();
  const v: EntityView = {
    id,
    kind,
    owner,
    state: at(ENTITY_VIEW_STATES, st & ~DETAIL_BIT, 'entity state'),
    x: posFromWire(r.i16()),
    y: posFromWire(r.i16()),
    vx: velFromWire(r.i16()),
    vy: velFromWire(r.i16()),
    segTick: r.u32(),
    extra: r.u8(),
  };
  if (st & DETAIL_BIT) v.detail = readDetail(r);
  return v;
}

function readGhost(r: Reader): Ghost {
  const id = r.varint();
  const kind = at(ENTITY_KINDS, r.u8(), 'entity kind');
  const owner = r.u8();
  const flags = r.u8();
  const g: Ghost = { id, kind, owner, x: posFromWire(r.i16()), y: posFromWire(r.i16()), tick: r.varint() };
  if (flags & 1) g.heading = angleFromWire(r.u16());
  if (flags & 2) g.extra = r.u8();
  return g;
}

export function decodeSnapshot(data: ArrayBuffer | Uint8Array): NetSnapshot {
  const r = new Reader(data instanceof Uint8Array ? data : new Uint8Array(data));
  const type = r.u8();
  if (type !== MSG_SNAP && type !== MSG_FULL) throw new Error(`codec: unknown message type ${type}`);
  const tick = r.u32();
  const credits = r.i32();
  const incomePerMin = r.i16();
  const areaShare = shareFromWire(r.u16());
  const flags = r.u8();
  const focusW = r.u16();
  const fr = r.u8();
  const rateW = r.u8();
  const front = { status: at(FRONT_STATUSES, fr >> 6, 'front status'), ratio: frontRatioFromWire(fr & 63), rate: rateW > 127 ? rateW - 256 : rateW };
  const hdr: NetHeader = { tick, credits, incomePerMin, areaShare, flags, focus: focusW === NO_FOCUS ? -1 : focusW, front };
  const s = emptySnapshot(type === MSG_FULL, hdr);
  const mask = r.u16();
  const has = (i: number) => (mask & (1 << i)) !== 0;

  if (has(S_OWNERS)) {
    const n = r.varint();
    if (n > MAX_HEXES) throw new RangeError('codec: too many owners');
    const mode = r.u8();
    if (mode === 1) {
      let h = 0;
      while (h < n) {
        const owner = r.u8();
        const len = r.varint();
        if (len === 0 || h + len > n) throw new RangeError('codec: bad owner run');
        for (let k = 0; k < len; k++) s.owners.push({ hex: h++, owner });
      }
    } else {
      if (n * 3 > r.buf.length - r.o) throw new RangeError('codec: count exceeds buffer');
      for (let i = 0; i < n; i++) s.owners.push({ hex: r.u16(), owner: r.u8() });
    }
  }
  if (has(S_POPRAD)) {
    const n = r.varint();
    if (n > MAX_HEXES) throw new RangeError('codec: too many popRad entries');
    if (r.u8() === 1) {
      const bytes = readPackBits(r, n);
      for (let i = 0; i < n; i++) s.popRad.push({ hex: i, dens: bytes[i] >> 4, rad: bytes[i] & 15 });
    } else {
      if (n * 3 > r.buf.length - r.o) throw new RangeError('codec: count exceeds buffer');
      for (let i = 0; i < n; i++) {
        const hex = r.u16();
        const b = r.u8();
        s.popRad.push({ hex, dens: b >> 4, rad: b & 15 });
      }
    }
  }
  if (has(S_UPSERTS)) {
    const n = r.count(20);
    for (let i = 0; i < n; i++) s.upserts.push(readUpsert(r));
  }
  if (has(S_REMOVES)) {
    const n = r.count(5);
    for (let i = 0; i < n; i++) s.removes.push({ id: r.u32(), reason: at(REMOVE_REASONS, r.u8(), 'remove reason') });
  }
  if (has(S_TRACKS)) {
    const n = r.count(20);
    for (let i = 0; i < n; i++) {
      s.tracks.push({
        id: r.u32(),
        kind: at(ENTITY_KINDS, r.u8(), 'missile kind') as MissileKind,
        owner: r.u8(),
        ox: posFromWire(r.i16()),
        oy: posFromWire(r.i16()),
        tx: posFromWire(r.i16()),
        ty: posFromWire(r.i16()),
        launchTick: r.u32(),
        flightTicks: r.u16(),
      });
    }
  }
  if (has(S_HINTS)) {
    const n = r.count(15);
    for (let i = 0; i < n; i++) {
      s.hints.push({
        hintId: r.u32(),
        x: posFromWire(r.i16()),
        y: posFromWire(r.i16()),
        bearing: angleFromWire(r.u16()),
        spread: spreadFromWire(r.u8()),
        tick: r.u32(),
      });
    }
  }
  if (has(S_DETS)) {
    const n = r.count(9);
    for (let i = 0; i < n; i++) s.detonations.push({ x: posFromWire(r.i16()), y: posFromWire(r.i16()), tick: r.u32(), exact: r.u8() === 1 });
  }
  if (has(S_INTERCEPTS)) {
    const n = r.count(17);
    for (let i = 0; i < n; i++) {
      const shooterId = r.u32();
      const targetId = r.u32();
      const fireTick = r.u32();
      const resolveTick = r.u32();
      const b = r.u8();
      const hit = b & ~HINT_FLAG;
      const ie: NetSnapshot['intercepts'][number] = hit === HIT_PENDING ? { shooterId, targetId, fireTick, resolveTick } : { shooterId, targetId, fireTick, resolveTick, hit: hit === 1 };
      if (b & HINT_FLAG) {
        ie.ox = posFromWire(r.i16());
        ie.oy = posFromWire(r.i16());
      }
      s.intercepts.push(ie);
    }
  }
  if (has(S_SHARES)) {
    const n = r.count(5);
    for (let i = 0; i < n; i++) s.shares.push({ playerId: r.u8(), areaShare: shareFromWire(r.u16()), score: r.u16() });
  }
  if (has(S_PHASE)) {
    const n = r.count(7);
    for (let i = 0; i < n; i++) s.phase.push({ kind: at(PHASE_KINDS, r.u8(), 'phase kind'), a: r.u8(), b: r.u8(), tick: r.u32() });
  }
  if (has(S_LIVE)) {
    const runs = r.count(2);
    let prevEnd = 0;
    for (let i = 0; i < runs; i++) {
      const start = prevEnd + r.varint();
      const len = r.varint();
      if (len === 0 || start + len > MAX_HEXES) throw new RangeError('codec: bad live run');
      for (let k = 0; k < len; k++) s.live.push(start + k);
      prevEnd = start + len;
    }
  }
  if (has(S_GHOSTS)) {
    const n = r.count(9);
    for (let i = 0; i < n; i++) s.ghosts.push(readGhost(r));
  }
  if (has(S_PROMPTS)) {
    const n = r.count(6);
    for (let i = 0; i < n; i++) s.allyPrompts.push({ from: r.u8(), to: r.u8(), expiresTick: r.u32() });
  }
  if (has(S_LASTSEEN)) {
    const n = r.varint();
    if (n > MAX_HEXES) throw new RangeError('codec: lastSeen too long');
    const seen = new Int32Array(n);
    let h = 0;
    while (h < n) {
      const v = r.varint();
      const len = r.varint();
      if (len === 0 || h + len > n) throw new RangeError('codec: bad lastSeen run');
      seen.fill(v === 0 ? -1 : (v - 1) * 10, h, h + len);
      h += len;
    }
    s.lastSeen = seen;
  }
  if (r.o !== r.buf.length) throw new RangeError('codec: trailing bytes');
  return s;
}

